// src/store/cartStore.js
import { create } from "zustand";
import {
  getCart,
  addItemCart,
  removeItemCart,
  updateItemQuantity,
} from "../api/cartApi";

// ---------- helpers ----------
const unitPrice = (p) =>
  p.discounted_price && p.discounted_price !== 0 ? p.discounted_price : p.price;

const calcTotal = (cart) =>
  cart.reduce((sum, it) => sum + unitPrice(it.product) * it.quantity, 0);

const sameLine = (a, b) =>
  /* 1st preference: API gives a stable cart-row id */
  (a.id && b.id && a.id === b.id) ||
  /* fallback: product + size combo */
  (a.product.id === b.product.id &&
    (a.size?.id || a.size) === (b.size?.id || b.size));
// --------------------------------

const useCartStore = create((set, get) => ({
  cart: [],
  total_price: 0,
  error: null,

  /* ------------- LOAD CART ------------- */
  initializeCart: async () => {
    try {
      const cartData = await getCart(); // { data: [...], total_price?: n }
      const cartArr = cartData.data || [];
      set({ cart: cartArr, total_price: cartData.total_price ?? calcTotal(cartArr) });
    } catch (err) {
      set({ error: err.message });
    }
  },

  /* you rarely need this — but kept for completeness */
  setCart: (newCart) =>
    set({ cart: newCart, total_price: calcTotal(newCart) }),

  /* ------------- ADD ------------- */
  addToCart: async (item, setIsLoading = () => {}) => {
    try {
      setIsLoading(true);
      await addItemCart(item); // server adds / updates accordingly

      set((state) => {
        const idx = state.cart.findIndex((ci) => sameLine(ci, item));
        let newCart = [...state.cart];

        if (idx !== -1) {
          // bump quantity if the same product-size already exists
          newCart[idx] = {
            ...newCart[idx],
            quantity: newCart[idx].quantity + item.quantity,
          };
        } else {
          newCart.push(item);
        }

        return { cart: newCart, total_price: calcTotal(newCart) };
      });
    } catch (err) {
      set({ error: err.message });
    } finally {
      setIsLoading(false);
    }
  },

  /* ------------- REMOVE ------------- */
  removeFromCart: async (item) => {
    try {
      await removeItemCart(item); // server delete (ignore result)

      set((state) => {
        const newCart = state.cart.filter((ci) => !sameLine(ci, item));
        return { cart: newCart, total_price: calcTotal(newCart) };
      });
    } catch (err) {
      set({ error: err.message });
    }
  },

  /* ------------- INCREASE ------------- */
  increaseQtn: async (item) => {
    try {
      await updateItemQuantity({ id: item.id, quantity: item.quantity + 1 });

      set((state) => {
        const newCart = state.cart.map((ci) =>
          sameLine(ci, item) ? { ...ci, quantity: ci.quantity + 1 } : ci
        );
        return { cart: newCart, total_price: calcTotal(newCart) };
      });
    } catch (err) {
      set({ error: err.message });
    }
  },

  /* ------------- DECREASE ------------- */
  decreaseQtn: async (item) => {
    try {
      if (item.quantity <= 1) {
        // remove line entirely
        get().removeFromCart(item);
        return;
      }

      await updateItemQuantity({ id: item.id, quantity: item.quantity - 1 });

      set((state) => {
        const newCart = state.cart.map((ci) =>
          sameLine(ci, item) ? { ...ci, quantity: ci.quantity - 1 } : ci
        );
        return { cart: newCart, total_price: calcTotal(newCart) };
      });
    } catch (err) {
      set({ error: err.message });
    }
  },
}));

export default useCartStore;
